import { create } from 'zustand';

interface Project {
  spec_id: string;
  project_name: string;
  created_by: string;
  created_at: string;
  status: 'active' | 'completed' | 'draft';
  media_count?: number;
  analysis_progress?: number;
}

interface Annotation {
  annotation_id: string;
  chunk_id: string;
  question_id: string;
  answer_value: any;
  model_confidence: number;
  human_edited: boolean;
  chunk_text: string;
  media_title: string;
  media_type: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  citations?: any[];
  flagged?: boolean;
}

interface StudyTemplate {
  template_id: string;
  template_name: string;
  description: string;
  media_types: Array<{ label: string; value: string; file_extensions: string[] }>;
  content_categories: Array<{ label: string; value: string; description?: string }>;
  default_analysis_templates: string[];
  created_by: string;
  is_public: boolean;
}

interface AppState {
  // Projects
  projects: Project[];
  currentProject: Project | null;
  setProjects: (projects: Project[]) => void;
  setCurrentProject: (project: Project | null) => void;
  
  // Study Templates
  studyTemplates: StudyTemplate[];
  selectedTemplate: StudyTemplate | null;
  setStudyTemplates: (templates: StudyTemplate[]) => void;
  setSelectedTemplate: (template: StudyTemplate | null) => void;
  
  // Dynamic Options (based on current study/template)
  currentMediaTypes: Array<{ label: string; value: string; file_extensions: string[] }>;
  currentContentCategories: Array<{ label: string; value: string; description?: string }>;
  setCurrentMediaTypes: (types: Array<{ label: string; value: string; file_extensions: string[] }>) => void;
  setCurrentContentCategories: (categories: Array<{ label: string; value: string; description?: string }>) => void;
  
  // Annotations
  annotations: Annotation[];
  setAnnotations: (annotations: Annotation[]) => void;
  updateAnnotation: (annotationId: string, updates: Partial<Annotation>) => void;
  
  // Chat
  chatMessages: ChatMessage[];
  setChatMessages: (messages: ChatMessage[]) => void;
  addChatMessage: (message: ChatMessage) => void;
  flagMessage: (messageId: string) => void;
  
  // UI State
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const useStore = create<AppState>((set) => ({
  // Projects
  projects: [],
  currentProject: null,
  setProjects: (projects) => set({ projects }),
  setCurrentProject: (project) => set({ currentProject: project }),
  
  // Study Templates
  studyTemplates: [],
  selectedTemplate: null,
  setStudyTemplates: (templates) => set({ studyTemplates: templates }),
  setSelectedTemplate: (template) => set({ selectedTemplate: template }),
  
  // Dynamic Options
  currentMediaTypes: [],
  currentContentCategories: [],
  setCurrentMediaTypes: (types) => set({ currentMediaTypes: types }),
  setCurrentContentCategories: (categories) => set({ currentContentCategories: categories }),
  
  // Annotations
  annotations: [],
  setAnnotations: (annotations) => set({ annotations }),
  updateAnnotation: (annotationId, updates) =>
    set((state) => ({
      annotations: state.annotations.map((ann) =>
        ann.annotation_id === annotationId ? { ...ann, ...updates } : ann
      ),
    })),
  
  // Chat
  chatMessages: [],
  setChatMessages: (messages) => set({ chatMessages: messages }),
  addChatMessage: (message) =>
    set((state) => ({
      chatMessages: [...state.chatMessages, message],
    })),
  flagMessage: (messageId) =>
    set((state) => ({
      chatMessages: state.chatMessages.map((msg) =>
        msg.id === messageId ? { ...msg, flagged: !msg.flagged } : msg
      ),
    })),
  
  // UI State
  loading: false,
  setLoading: (loading) => set({ loading }),
})); 